import { useState } from 'react'
import type { Entry } from 'contentful'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'

import { buttonStyles, type VariantType } from '@/components/ui/cta-link'

import { cx } from '@/utils/strings'
import useMutationObserver from '@/utils/useMutationObserver'
import type {
  TypeComponentCtaLinkSkeleton,
  TypeComponentHubspotCtaSkeleton,
  TypeSectionCtaSkeleton,
  TypeComponentLottieSkeleton,
} from '@/types/ctf'
import { useEventListener } from 'usehooks-ts'

import { getBackground } from '@/utils/backgrounds'
import gradients from '@/constants/gradients'
import Lottie from '../lottie'
import Image from '@/components/ui/image'

type LottieEntry = Entry<TypeComponentLottieSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SectionCtaEntry = Entry<TypeSectionCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ComponentCtaLinkEntry = Entry<TypeComponentCtaLinkSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ComponentHubspotCtaEntry = Entry<TypeComponentHubspotCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SectionCtaProps = SectionCtaEntry['fields'] & {
  className?: string
}

export default function Cta({
  actions,
  backgroundColor: _backgroundColor,
  backgroundImage,
  body,
  className,
  eyebrowText,
  headline,
  image,
  sticky,
  textAlign = 'Left',
  asset,
}: SectionCtaProps): JSX.Element {
  const backgroundColor = _backgroundColor || gradients.midnight

  const [headerHeight, setHeaderHeight] = useState(0)
  const calcHeaderHeight = () => {
    const header = document.querySelector('header')
    if (header) {
      const height = header.offsetHeight
      setHeaderHeight(height)
    }
  }
  useMutationObserver('header', calcHeaderHeight)
  useEventListener('resize', calcHeaderHeight)

  const renderCTA = (
    action: ComponentCtaLinkEntry | ComponentHubspotCtaEntry | undefined,
    btnStyle: VariantType,
    scrollToOffset = 0
  ) => {
    if (!action) return null
    const type = action.sys.contentType.sys.id
    const fields = {
      type,
      ...action.fields,
    } as DynamicActionProps

    return (
      <DynamicAction
        key={action.sys.id}
        {...fields}
        style={sticky ? btnStyle : fields.style}
        hasDarkBg={!sticky}
        scrollToOffset={scrollToOffset}
        id={action.sys.id}
      />
    )
  }

  return sticky ? (
    <section
      id="sticky-cta"
      className={cx('max-w-8xl mx-auto sm:sticky z-20 px-0 lg:px-9 w-fit', className)}
      style={headerHeight ? { top: `${headerHeight + 12}px` } : undefined}
    >
      {actions && (
        <div className="bg-white sm:bg-transparent">
          <div
            className={cx(
              'flex flex-col shrink-0 flex-wrap gap-0 sm:gap-4 sm:flex-row sm:rounded-full p-3 sm:shadow-even-around bg-white dark:bg-secondary-800',
              (textAlign === 'Center' || !textAlign) && 'items-center',
              textAlign === 'Left' && 'items-start',
              textAlign === 'Right' && 'items-end'
            )}
            data-testid="actions"
          >
            {actions.map((action: any, idx) => (
              <div key={idx}>
                <div className="block sm:hidden">{renderCTA(action, buttonStyles.btnArrow)}</div>
                <div className={cx(sticky ? `hidden sm:flex` : `flex`, `items-center relative`)}>
                  {renderCTA(action, buttonStyles.btnSolid, 55)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  ) : (
    <section className={cx('max-w-8xl mx-auto px-5 lg:px-9', className)}>
      <div
        className={cx(
          backgroundColor && getBackground(backgroundColor),
          'rounded-lg dark-default-gradient p-6 lg:p-10 relative'
        )}
      >
        <div className="z-10 relative">
          {(body || eyebrowText || headline || actions) && (
            <div className={cx((image || asset) && 'flex flex-col gap-8 flex-col-reverse md:flex-row items-center')}>
              <div
                className={cx(
                  (image || asset) && 'md:w-2/3',
                  (textAlign === 'Center' || !textAlign) && 'text-center',
                  textAlign === 'Left' && 'text-left',
                  textAlign === 'Right' && 'text-right'
                )}
              >
                {eyebrowText && <p className="text-eyebrow uppercase mb-6 md:mb-8">{eyebrowText}</p>}
                {headline && <h3 className="mb-7">{headline}</h3>}
                {body && (
                  <div className="[&_h2]:h6 [&_h2]:m-0 [&_h3]:h6 [&_h3]:m-0 [&_h4]:h6 [&_h4]:m-0 [&_p]:m-0 [&_p]:[text-wrap:balance] mb-6">
                    <RichText body={body} />
                  </div>
                )}
                {actions && (
                  <div
                    className={cx(
                      'flex flex-col shrink-0 flex-wrap gap-3 flex-row w-full items-center',
                      (textAlign === 'Center' || !textAlign) && 'items-center justify-center'
                    )}
                    data-testid="actions"
                  >
                    {actions.map((action: any) => (
                      <div key={action.sys.id} className="w-full sm:w-auto">
                        <div className="items-center relative dark:after:bg-secondary-500">
                          {renderCTA(action, buttonStyles.btnSolid, 50)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {image && (
                <div className="md:w-1/3 [&_svg]:fill-none [&_figure]:m-0">
                  <Image image={image} />
                </div>
              )}
              {asset && !image && (
                <>
                  {asset?.sys?.contentType?.sys?.id === 'componentLottie' && (
                    <div className="md:w-1/3 [&_svg]:fill-none [&_figure]:m-0">
                      <Lottie {...(asset as LottieEntry)?.fields} />
                    </div>
                  )}
                  {asset?.sys?.contentType?.sys?.id === 'resourceImage' && (
                    <div className="md:w-1/3 [&_svg]:fill-none [&_figure]:m-0 [&_svg]:w-full [&_svg]:h-auto">
                      <Image {...(asset?.fields as any)} darkModeImage={asset.imageDarkMode?.fields} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {backgroundImage && (
          <div
            className="rounded-lg absolute left-0 right-0 top-0 bottom-0 w-full h-full z-0 bg-cover bg-center bg-no-repeat "
            style={backgroundImage ? { backgroundImage: `url(${backgroundImage.fields.file?.url})` } : undefined}
          />
        )}
      </div>
    </section>
  )
}
